import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ThumbsUp, Flag, PenSquare, Loader2 } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import { useAuth } from "@/context/AuthContext";

/**
 * Public-facing reviews section for a program.
 * If `alumnoContext.programId` is provided AND user is authed as alumno,
 * exposes the write-review CTA.
 */
export default function ReviewsSection({ slug, programId }) {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [mine, setMine] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get(`/public/programs/${slug}/reviews/summary`),
        api.get(`/public/programs/${slug}/reviews?page=${page}&sort=${sort}`),
      ]);
      setSummary(s.data);
      setList(l.data);
      if (user && programId) {
        try {
          const r = await api.get(`/alumno/programs/${programId}/reviews/mine`);
          setMine(r.data);
        } catch { /* silent */ }
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, sort, page]);

  const totalReviews = summary?.count || 0;
  const avg = summary?.avg_rating || 0;
  const dist = summary?.distribution || { "5": 0, "4": 0, "3": 0, "2": 0, "1": 0 };
  const maxD = Math.max(...Object.values(dist), 1);

  return (
    <section className="mt-12 border-t border-white/10 pt-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-white font-display">Opiniones de alumnos</h2>
        {user && mine?.eligible && (
          <button onClick={() => setShowWrite(true)}
                  data-testid="reviews-write-cta"
                  className="inline-flex items-center gap-1 rounded-md bg-[#F26A21] px-3 py-1.5 text-sm font-semibold text-[#0B0F14] hover:brightness-110">
            <PenSquare className="w-3 h-3" /> {mine.review ? "Editar mi opinión" : "Escribir opinión"}
          </button>
        )}
        {user && !mine?.eligible && mine && (
          <p className="text-[11px] text-gray-500" data-testid="reviews-ineligible-note">
            Reseña disponible al alcanzar 80% de avance (actual: {mine.progress_pct}%).
          </p>
        )}
      </div>

      {totalReviews === 0 ? (
        <p className="mt-6 text-sm text-gray-400" data-testid="reviews-empty">
          Aún no hay opiniones. Sé el primero cuando completes el programa.
        </p>
      ) : (
        <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-6">
          <aside className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="reviews-summary">
            <p className="text-5xl font-bold text-white">{avg.toFixed(1)}</p>
            <RatingStars value={avg} showCount={false} size="lg" />
            <p className="mt-1 text-xs text-gray-400">{totalReviews} opinion{totalReviews > 1 ? "es" : ""}</p>
            <div className="mt-4 space-y-1">
              {[5, 4, 3, 2, 1].map((n) => (
                <div key={n} className="flex items-center gap-2">
                  <span className="text-[11px] text-gray-400 w-3 text-right">{n}★</span>
                  <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-[#F26A21] transition-all" style={{ width: `${(dist[String(n)] / maxD) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-500 w-6 text-right">{dist[String(n)] || 0}</span>
                </div>
              ))}
            </div>
          </aside>

          <div>
            <div className="flex items-center justify-between mb-3">
              <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
                      data-testid="reviews-sort"
                      className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white">
                <option value="recent">Más recientes</option>
                <option value="helpful">Más útiles</option>
                <option value="rating_desc">Mejor calificados</option>
                <option value="rating_asc">Peor calificados</option>
              </select>
            </div>
            {loading ? (
              <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
            ) : (
              <div className="space-y-4">
                {list.items.map((r) => (
                  <ReviewCard key={r.id} review={r} onChanged={load} authed={!!user} />
                ))}
              </div>
            )}
            {list.total > 10 && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                        className="text-xs text-gray-400 disabled:opacity-40 hover:text-white">← Anterior</button>
                <span className="text-xs text-gray-400">Página {page}</span>
                <button disabled={page * 10 >= list.total} onClick={() => setPage(p => p + 1)}
                        className="text-xs text-gray-400 disabled:opacity-40 hover:text-white">Siguiente →</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showWrite && (
        <WriteModal existing={mine?.review} programId={programId}
                    onClose={() => setShowWrite(false)}
                    onSaved={() => { setShowWrite(false); load(); }} />
      )}
    </section>
  );
}

function ReviewCard({ review, onChanged, authed }) {
  const [voting, setVoting] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const helpful = async () => {
    if (!authed) { toast.error("Inicia sesión para marcar como útil."); return; }
    setVoting(true);
    try {
      await api.post(`/alumno/reviews/${review.id}/helpful`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setVoting(false); }
  };
  const flag = async () => {
    if (!authed) { toast.error("Inicia sesión para reportar."); return; }
    if (!confirm("¿Reportar esta opinión como inapropiada?")) return;
    setFlagging(true);
    try {
      await api.post(`/alumno/reviews/${review.id}/flag`);
      toast.success("Reporte enviado.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setFlagging(false); }
  };
  return (
    <article className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`review-card-${review.id}`}>
      <header className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#F26A21]/20 text-[#F26A21] flex items-center justify-center text-xs font-bold">
          {(review.user_name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{review.user_name}</p>
          <div className="flex items-center gap-2">
            <RatingStars value={review.rating} showCount={false} size="xs" />
            <span className="text-[11px] text-gray-500">{new Date(review.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric" })}</span>
          </div>
        </div>
      </header>
      <h3 className="mt-3 text-sm font-bold text-white">{review.title}</h3>
      <p className="mt-1 text-sm text-gray-300 whitespace-pre-wrap">{review.body}</p>
      {(review.pros?.length > 0 || review.cons?.length > 0) && (
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          {review.pros?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">Pros</p>
              <ul className="mt-1 space-y-0.5 text-xs text-gray-300">
                {review.pros.map((p, i) => <li key={i}>+ {p}</li>)}
              </ul>
            </div>
          )}
          {review.cons?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">Áreas de mejora</p>
              <ul className="mt-1 space-y-0.5 text-xs text-gray-300">
                {review.cons.map((c, i) => <li key={i}>– {c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <footer className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
        <button onClick={helpful} disabled={voting}
                data-testid={`review-helpful-${review.id}`}
                className="inline-flex items-center gap-1 hover:text-white">
          <ThumbsUp className="w-3 h-3" /> Útil ({review.helpful_count})
        </button>
        <button onClick={flag} disabled={flagging}
                data-testid={`review-flag-${review.id}`}
                className="inline-flex items-center gap-1 hover:text-red-300">
          <Flag className="w-3 h-3" /> Reportar
        </button>
      </footer>
    </article>
  );
}

function WriteModal({ existing, programId, onClose, onSaved }) {
  const [rating, setRating] = useState(existing?.rating || 5);
  const [title, setTitle] = useState(existing?.title || "");
  const [body, setBody] = useState(existing?.body || "");
  const [pros, setPros] = useState((existing?.pros || []).join("\n"));
  const [cons, setCons] = useState((existing?.cons || []).join("\n"));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || body.trim().length < 10) {
      toast.error("Título mín 3 chars · Cuerpo mín 10 chars.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/alumno/programs/${programId}/reviews`, {
        rating,
        title: title.trim(),
        body: body.trim(),
        pros: pros.split("\n").map(s => s.trim()).filter(Boolean),
        cons: cons.split("\n").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Opinión publicada. ¡Gracias!");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.code === "review_not_eligible") {
        toast.error(d.message);
      } else toast.error(formatApiError(d));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            data-testid="review-write-modal"
            className="w-full max-w-lg rounded-2xl bg-[#111827] border border-white/10 p-6 max-h-[92vh] overflow-y-auto">
        <h2 className="text-xl font-bold text-white">{existing ? "Editar opinión" : "Escribir opinión"}</h2>
        <div className="mt-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-gray-300 mb-1">Tu calificación</p>
            <RatingStars value={rating} interactive onChange={setRating} size="xl" showCount={false} />
          </div>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-300 mb-1">Título</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
                   data-testid="review-title-input"
                   className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                   placeholder="Resumen en una línea" required />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-gray-300 mb-1">Tu experiencia</span>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} maxLength={4000}
                      data-testid="review-body-input"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white"
                      placeholder="Cuenta qué te aportó el programa, cómo aplicaste el aprendizaje…" required />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-emerald-300 mb-1">Pros (uno por línea)</span>
              <textarea value={pros} onChange={(e) => setPros(e.target.value)} rows={3}
                        data-testid="review-pros-input"
                        className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-xs text-white" />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-amber-300 mb-1">Áreas de mejora</span>
              <textarea value={cons} onChange={(e) => setCons(e.target.value)} rows={3}
                        data-testid="review-cons-input"
                        className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-xs text-white" />
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="px-4 py-2 rounded-md border border-white/20 text-white text-sm">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="review-submit-btn"
                  className="px-4 py-2 rounded-md bg-[#F26A21] text-[#0B0F14] font-semibold text-sm disabled:opacity-50 inline-flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Publicar
          </button>
        </div>
      </form>
    </div>
  );
}
